import { inject, Injectable, linkedSignal, Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { EMPTY, map, tap } from 'rxjs';
import { LoggerService } from '@shared/service-logger';
import { UserEntity, UserListItem, UserV2 } from 'shared-acl-model-user';
import { USER_ENDPOINTS } from './user.endpoints';
import { UserListItemDto, UserV2Dto } from './user.dto';
import { AuthService } from '@suite/shared';
import { CustomerService } from '@shared/customer-service-customer';
import { Customer } from '@shared/customer-model-customer';

@Injectable({ providedIn: 'root' })
export class UserDataProvider {
  private readonly authService = inject(AuthService);
  private readonly customerService = inject(CustomerService);
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);

  /**
   * @deprecated Use createUserResourceV2 instead
   */
  userResource = rxResource<UserEntity, { isLoggedIn: boolean; selectedCustomer: Customer | null }>({
    params: () => ({
      isLoggedIn: this.authService.isLoggedIn(),
      selectedCustomer: this.customerService.selectedCustomer(),
    }),
    stream: () => {
      return this.fetchUser();
    },
  });

  /**
   * @deprecated createUserResourceV2 instead.
   */
  user = linkedSignal({
    source: () => (this.userResource.hasValue() ? this.userResource.value() : undefined),
    computation: (data) => data ?? undefined,
  });

  /**
   * Creates a resource for fetching v2 user data.
   * @param isLoggedIn Signal indicating if user is logged in
   * @param selectedCustomer Signal of the selected customer (triggers refetch on change)
   * @returns A resource for v2 user data
   */
  createUserResourceV2(isLoggedIn: Signal<boolean>, selectedCustomer: Signal<unknown>) {
    return rxResource({
      params: () => {
        if (!isLoggedIn()) {
          return;
        }
        return { selectedCustomer: selectedCustomer() };
      },
      stream: () => this.fetchUserV2(),
    });
  }

  createUsersResource(customerId: Signal<number | null>) {
    return rxResource({
      params: () => ({ customerId: customerId() }),
      stream: ({ params }) => (params.customerId ? this.fetchUsers() : EMPTY),
    });
  }

  /**
   * @deprecated Use fetchUserV2 instead
   */
  private fetchUser() {
    this.logger.debug('Fetching user (v1, deprecated)', 'UserDataProvider');
    return this.http
      .get<UserEntity>(USER_ENDPOINTS.fetchUser)
      .pipe(tap(() => this.logger.info('Fetched user (v1)', 'UserDataProvider')));
  }

  private fetchUserV2() {
    this.logger.debug('Fetching user (v2)', 'UserDataProvider');
    return this.http.get<UserV2Dto>(USER_ENDPOINTS.fetchUserV2).pipe(
      tap(() => this.logger.info('Fetched user (v2)', 'UserDataProvider')),
      map((dto) => this.mapToUserV2(dto)),
    );
  }

  private fetchUsers() {
    this.logger.debug('Fetching users', 'UserDataProvider');
    return this.http.get<UserListItemDto[]>(USER_ENDPOINTS.fetchUsers).pipe(
      tap(() => this.logger.info('Fetched users', 'UserDataProvider')),
      map((dtos) => dtos.map((dto) => this.mapToUserListItem(dto))),
    );
  }

  private mapToUserV2(dto: UserV2Dto): UserV2 {
    return new UserV2({
      id: dto.id,
      firstName: dto.firstName,
      lastName: dto.lastName,
      email: dto.email,
      phoneNumber: dto.phoneNumber,
      company: dto.company,
      avatarUrl: dto.avatarUrl,
      validFrom: new Date(dto.validFrom),
      validTo: dto.validTo ? new Date(dto.validTo) : null,
      accessRights: {
        privileges: dto.accessRights.privileges.map((p) => ({
          id: p.id,
          name: p.name,
        })),
        resources: dto.accessRights.resources.map((r) => ({
          id: r.id,
          resourceType: r.resourceType,
          privileges: r.privileges.map((rp) => ({
            id: rp.id,
            actions: rp.actions,
          })),
        })),
      },
    });
  }

  private mapToUserListItem(dto: UserListItemDto): UserListItem {
    return new UserListItem({
      id: dto.id,
      firstName: dto.firstName,
      lastName: dto.lastName,
      email: dto.email,
      phoneNumber: dto.phoneNumber,
      company: dto.company,
      avatarUrl: dto.avatarUrl,
      validFrom: new Date(dto.validFromMs),
      validTo: dto.validToMs ? new Date(dto.validToMs) : null,
      active: dto.active,
    });
  }
}
